import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Loader2, AlertTriangle, ArrowRight, CheckCircle2, History } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/** Admin-only tool: reconcile subtopic labels that got split across upload batches. */
export default function AdminLabels() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null); // { topic, from_label, to_label, count }
  const [running, setRunning] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/label-mismatches", { params: { exam_board: "AQA", subject: "Psychology" } })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const merge = async () => {
    if (!pending) return;
    setRunning(true);
    try {
      const { data: res } = await api.post("/admin/merge-labels", {
        topic: pending.topic, from_label: pending.from_label, to_label: pending.to_label,
        exam_board: "AQA", subject: "Psychology",
      });
      toast.success(res.idempotent_no_op
        ? `Rule saved (no questions to move — already merged)`
        : `Merged ${res.question_count_moved} question(s) into "${pending.to_label}"`);
      setPending(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setRunning(false);
    }
  };

  const merges = data?.recent_merges || [];
  const totalMismatchTopics = data?.total_mismatched_topics || 0;

  if (!isAdmin) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-24 text-center" data-testid="admin-labels-forbidden">
        <p className="text-muted-foreground">This tool is admin-only.</p>
        <Link to="/dashboard" className="text-primary text-sm mt-3 inline-block">Back to Dashboard</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12" data-testid="admin-labels-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-2">Admin</p>
      <h1 className="text-4xl font-bold tracking-tighter">Subtopic Label Reconciliation</h1>
      <p className="mt-3 text-muted-foreground max-w-3xl">
        Same spec content sometimes gets extracted under different subtopic phrasing across upload batches (e.g. 2017-2019 vs 2022-2025).
        This splits a subtopic&rsquo;s question history, which makes watchlist rows show <span className="italic">&ldquo;No major essay in analysed papers&rdquo;</span> when
        the essay actually exists under the other spelling. Merges are recorded — future uploads carrying the old label are auto-remapped.
      </p>

      <div className="mt-6 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 flex gap-3 items-start" data-testid="admin-labels-warning">
        <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          Only merge when both labels refer to the same specification subtopic. Some pairs listed here are <span className="font-semibold text-foreground">genuinely
          different</span> subtopics — leave those alone. The canonical name should match the exact wording of the official AQA 7182 specification.
        </p>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <Badge variant="outline" data-testid="admin-labels-topic-count">{totalMismatchTopics} topics with mismatches</Badge>
        <Button variant="outline" size="sm" className="rounded-full" onClick={load} data-testid="admin-labels-refresh">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Refresh"}
        </Button>
      </div>

      {loading && (
        <div className="mt-10 text-center text-sm text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading mismatches…
        </div>
      )}

      {!loading && data && (
        <>
          <div className="mt-8 space-y-6" data-testid="mismatch-list">
            {data.mismatches.map((m) => (
              <TopicPairs key={m.topic} m={m} onMerge={setPending} />
            ))}
            {!data.mismatches.length && (
              <div className="rounded-3xl border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
                <CheckCircle2 className="h-6 w-6 text-emerald-500 mx-auto mb-2" />
                No mismatches — every subtopic label appears consistently across 2017-2019 and 2022-2025.
              </div>
            )}
          </div>

          {merges.length > 0 && (
            <div className="mt-10 rounded-3xl border border-border bg-card p-6" data-testid="merge-history">
              <h2 className="font-semibold tracking-tight flex items-center gap-2 mb-4">
                <History className="h-4 w-4 text-primary" />Recent merges ({merges.length})
              </h2>
              <div className="space-y-2">
                {merges.map((r) => (
                  <div key={r.id} className="flex items-center gap-2 text-xs text-muted-foreground" data-testid={`merge-entry-${r.id}`}>
                    <Badge variant="outline" className="text-[10px]">{r.topic}</Badge>
                    <span className="line-through">{r.from_label}</span>
                    <ArrowRight className="h-3 w-3 text-primary shrink-0" />
                    <span className="text-foreground">{r.to_label}</span>
                    <span className="ml-auto shrink-0">{r.question_count_moved || 0} moved</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <Dialog open={!!pending} onOpenChange={(v) => !v && setPending(null)}>
        <DialogContent data-testid="merge-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Merge subtopic labels?</DialogTitle>
            <DialogDescription>Under <span className="font-semibold text-foreground">{pending?.topic}</span></DialogDescription>
          </DialogHeader>
          {pending && (
            <div className="space-y-3 text-sm">
              <div className="rounded-xl bg-secondary p-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-1">From</p>
                <p className="font-medium" data-testid="merge-dialog-from">{pending.from_label}</p>
                <p className="text-xs text-muted-foreground mt-1">{pending.count} question(s) will be re-tagged</p>
              </div>
              <div className="flex justify-center"><ArrowRight className="h-4 w-4 text-primary" /></div>
              <div className="rounded-xl bg-primary/10 border border-primary/30 p-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-primary mb-1">To (canonical)</p>
                <p className="font-medium" data-testid="merge-dialog-to">{pending.to_label}</p>
              </div>
              <p className="text-xs text-muted-foreground">
                A rule is recorded so future uploads carrying the &ldquo;from&rdquo; label are auto-remapped. Reversible via manual re-merge in the opposite direction.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPending(null)} data-testid="merge-cancel">Cancel</Button>
            <Button onClick={merge} disabled={running} data-testid="merge-confirm">
              {running ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Merging…</> : "Confirm merge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TopicPairs({ m, onMerge }) {
  const [pickedTo, setPickedTo] = useState(""); // subtopic string picked as canonical
  const early = m.counts?.only_in_2017_2019 || [];
  const late = m.counts?.only_in_2022_2025 || [];
  const allLabels = useMemo(() => [...early, ...late], [early, late]);

  return (
    <div className="rounded-3xl border border-border bg-card p-6" data-testid={`topic-block-${m.topic.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-baseline justify-between gap-3 mb-4">
        <h3 className="font-semibold tracking-tight">{m.topic}</h3>
        <span className="text-xs text-muted-foreground">{early.length + late.length} unique labels not shared across batches</span>
      </div>
      <p className="text-xs text-muted-foreground mb-4">Step 1 — click a label below to select it as the canonical (destination) name. Step 2 — click &ldquo;Merge into&rdquo; on any other label that means the same thing.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <LabelColumn title="Only in 2017-2019" rows={early} pickedTo={pickedTo} setPickedTo={setPickedTo}
                     topic={m.topic} onMerge={onMerge} side="early" />
        <LabelColumn title="Only in 2022-2025" rows={late} pickedTo={pickedTo} setPickedTo={setPickedTo}
                     topic={m.topic} onMerge={onMerge} side="late" />
      </div>
      {pickedTo && !allLabels.some((r) => r.subtopic === pickedTo) && (
        <p className="mt-4 text-xs text-amber-500">Selected canonical label is from a different topic block — clear selection.</p>
      )}
    </div>
  );
}

function LabelColumn({ title, rows, pickedTo, setPickedTo, topic, onMerge, side }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-2">{title}</p>
      <div className="space-y-2">
        {rows.map((r, i) => {
          const isPicked = pickedTo === r.subtopic;
          return (
            <div key={i} className={`rounded-xl border p-3 transition-colors ${isPicked ? "border-primary/50 bg-primary/10" : "border-border bg-secondary/50"}`}
                 data-testid={`label-row-${side}-${i}`}>
              <div className="flex items-start justify-between gap-2">
                <button
                  className={`text-left text-xs font-medium flex-1 leading-snug ${isPicked ? "text-primary" : "hover:text-primary"}`}
                  onClick={() => setPickedTo(isPicked ? "" : r.subtopic)}
                  data-testid={`pick-canonical-${side}-${i}`}
                >
                  {r.subtopic}
                </button>
                <Badge variant="outline" className="text-[10px] shrink-0">{r.count}</Badge>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-[10px] text-muted-foreground">
                  {isPicked ? "Selected as canonical (to)" : "Click text to pick as canonical"}
                </span>
                {pickedTo && !isPicked && (
                  <Button
                    size="sm" variant="ghost" className="h-7 text-[11px] gap-1"
                    onClick={() => onMerge({ topic, from_label: r.subtopic, to_label: pickedTo, count: r.count })}
                    data-testid={`merge-into-${side}-${i}`}
                  >
                    Merge into <ArrowRight className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
